import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PostProjectTaskPhase, setProjectPhaseManageModalOpen } from '../../redux/taskSlice';
import { sortProjectPhases } from '../../utils/projectPhaseUtils';
import { Add16Regular, Delete16Regular } from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList } from '@fluentui/react-components';
import styles from './css/index.module.css';

const ProjectPhaseManagementMenu = ({ onPhaseSelect, children, taskData, selectedPhaseName }) => {
    const dispatch = useDispatch();
    const { projectPhases } = useSelector(state => state.task);
    const [newPhaseName, setNewPhaseName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [open, setOpen] = useState(false);
    const selectedItemRef = useRef(null);

    useEffect(() => {
        if (!open || !selectedPhaseName) return;
        const timer = setTimeout(() => {
            selectedItemRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 0);
        return () => clearTimeout(timer);
    }, [open, selectedPhaseName]);

    const getAllPhases = () => {
        const phaseMap = new Map();

        if (projectPhases && projectPhases.length > 0) {
            projectPhases.forEach(phase => {
                phaseMap.set(phase.name, { ...phase, source: 'project' });
            });
        }

        let tasks = [];
        if (Array.isArray(taskData)) {
            tasks = taskData;
        } else if (taskData && taskData.data && Array.isArray(taskData.data)) {
            tasks = taskData.data;
        } else if (taskData && taskData.groups && Array.isArray(taskData.groups)) {
            tasks = taskData.groups.flatMap(group => group.tasks || []);
        }

        tasks.forEach(task => {
            const phaseName = task?.project_phase;
            if (phaseName && phaseName.trim() !== '' && !phaseMap.has(phaseName)) {
                phaseMap.set(phaseName, {
                    id: `task_${phaseName}`,
                    name: phaseName,
                    source: 'task',
                });
            }
        });

        const values = Array.from(phaseMap.values());
        const projectPhaseList = sortProjectPhases(values.filter(phase => phase.source === 'project'));
        const taskOnlyList = values
            .filter(phase => phase.source === 'task')
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        return [...projectPhaseList, ...taskOnlyList];
    };

    const allPhases = getAllPhases();

    const handleAddPhase = async () => {
        if (!newPhaseName.trim()) return;

        setIsAdding(true);
        try {
            await dispatch(PostProjectTaskPhase({ name: newPhaseName.trim() }));
            setNewPhaseName('');
        } catch (error) {
            console.error('Error adding project phase:', error);
        } finally {
            setIsAdding(false);
        }
    };

    const handlePhaseClick = phase => {
        onPhaseSelect(phase);
        setOpen(false);
    };

    return (
        <Menu open={open} onOpenChange={(e, data) => setOpen(data.open)}>
            <MenuTrigger>{children}</MenuTrigger>
            <MenuPopover className={styles.entitySelectorPopover}>
                <MenuList className={styles.entitySelectorList}>
                    <div className={styles.entitySelectorItems}>
                        {allPhases.length > 0 ? (
                            allPhases.map(phase => {
                                const isSelected = !!selectedPhaseName && phase.name === selectedPhaseName;
                                return (
                                    <div
                                        key={phase.id}
                                        ref={isSelected ? selectedItemRef : undefined}
                                        onClick={() => handlePhaseClick(phase)}
                                        className={`${styles.entitySelectorItem} ${isSelected ? styles.entitySelectorItemSelected : ''}`}>
                                        <span className={styles.entitySelectorItemLabel}>{phase.name}</span>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={styles.entitySelectorEmpty}>No project phases available</div>
                        )}
                    </div>

                    {allPhases.length > 0 ? (
                        <>
                            <div className={styles.entitySelectorDivider} />
                            <div
                                className={styles.entitySelectorItem}
                                onClick={() => {
                                    dispatch(setProjectPhaseManageModalOpen(true));
                                    setOpen(false);
                                }}>
                                <span className={styles.entitySelectorItemLabel}>Edit Project Phases</span>
                            </div>
                        </>
                    ) : null}

                    {selectedPhaseName ? (
                        <>
                            <div className={styles.entitySelectorDivider} />
                            <div
                                className={styles.entitySelectorRemove}
                                onClick={() => {
                                    onPhaseSelect(null);
                                    setOpen(false);
                                }}>
                                <span className={styles.entitySelectorRemoveLabel}>Remove Project Phase</span>
                                <Delete16Regular className={styles.entitySelectorRemoveIcon} />
                            </div>
                        </>
                    ) : null}

                    <div className={styles.entitySelectorDivider} />
                    <div className={styles.entitySelectorAddRow}>
                        <input
                            type="text"
                            placeholder="Add new project phase"
                            className={styles.entitySelectorInput}
                            value={newPhaseName}
                            onChange={e => setNewPhaseName(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddPhase();
                                }
                            }}
                        />
                        <button
                            type="button"
                            className={styles.entitySelectorAddBtn}
                            onClick={handleAddPhase}
                            disabled={!newPhaseName.trim() || isAdding}
                            aria-label="Add project phase">
                            <Add16Regular />
                        </button>
                    </div>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default ProjectPhaseManagementMenu;
